import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, FileText, Calendar, AlertCircle, Loader2, Building2 } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { createLogger } from '../../utils/logger'

const logger = createLogger('TermsOfService')

// Manual frontmatter parser (browser-compatible)
const parseFrontmatter = (markdown) => {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n([\s\S]*)$/
  const match = markdown.match(frontmatterRegex)

  if (!match) {
    return { data: {}, content: markdown }
  }

  const frontmatterText = match[1]
  const content = match[2]

  const data = {}
  frontmatterText.split('\n').forEach((line) => {
    const colonIndex = line.indexOf(':')
    if (colonIndex !== -1) {
      const key = line.substring(0, colonIndex).trim()
      const value = line
        .substring(colonIndex + 1)
        .trim()
        .replace(/^["']|["']$/g, '')
      if (key) {
        data[key] = value
      }
    }
  })

  return { data, content }
}

const TermsOfService = () => {
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [title, setTitle] = useState('Terms of Service')
  const [termsVersion, setTermsVersion] = useState(import.meta.env.VITE_TERMS_VERSION || '1.0')
  const [effectiveDate, setEffectiveDate] = useState('January 1, 2025')
  const [lastUpdated, setLastUpdated] = useState('')
  const [platformName, setPlatformName] = useState('')
  const [owner, setOwner] = useState('')

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        setLoading(true)
        const response = await fetch('/terms_and_conditions.md')
        if (!response.ok) {
          throw new Error(`Failed to load terms: ${response.statusText}`)
        }
        const markdown = await response.text()

        // Parse frontmatter
        const { data, content: markdownContent } = parseFrontmatter(markdown)

        // Use frontmatter values if available, otherwise use defaults
        if (data.title) {
          setTitle(data.title)
        }
        if (data.effectiveDate) {
          setEffectiveDate(data.effectiveDate)
        }
        if (data.lastUpdated) {
          setLastUpdated(data.lastUpdated)
        }
        if (data.version) {
          setTermsVersion(data.version)
        }
        if (data.platformName) {
          setPlatformName(data.platformName)
        }
        if (data.owner) {
          setOwner(data.owner)
        }

        setContent(markdownContent)
        setError(null)
      } catch (err) {
        logger.error('Error loading terms:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchTerms()
  }, [])

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>

          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <FileText className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{title}</h1>
              {owner && (
                <div className="flex items-center gap-1.5 mb-3 text-gray-700 dark:text-gray-300">
                  <Building2 className="w-4 h-4" />
                  <span className="text-sm font-medium">{owner}</span>
                </div>
              )}
              <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  <span>Effective: {effectiveDate}</span>
                </div>
                {lastUpdated && (
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    <span>Last Updated: {lastUpdated}</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4" />
                  <span>Version {termsVersion}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
              <span className="ml-3 text-gray-600 dark:text-gray-400">
                Loading terms of service...
              </span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-900 dark:text-red-100">
                <strong>Error loading terms:</strong> {error}
              </p>
              <p className="text-sm text-red-700 dark:text-red-300 mt-2">
                Please try refreshing the page or contact support if the problem persists.
              </p>
            </div>
          )}

          {!loading && !error && content && (
            <div className="prose dark:prose-invert max-w-none prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-a:text-primary-600 dark:prose-a:text-primary-400 prose-strong:text-gray-900 dark:prose-strong:text-white prose-ul:text-gray-700 dark:prose-ul:text-gray-300 prose-ol:text-gray-700 dark:prose-ol:text-gray-300">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TermsOfService
